import { ApolloProvider } from '@apollo/client';
import { useSetAtom } from 'jotai';
import { createManagementApolloClient } from 'lib-knowledge-management-common/apolloClient';
import { LanguageContextProvider } from 'lib-knowledge-management-common/contexts/languageProvider';
import { ModalProvider } from 'lib-knowledge-modal';
import { KnowledgeErrorBoundary } from 'lib-knowledge-utils/components/bugTracking';
import { KnowledgeErrorPlaceholder } from 'lib-knowledge-utils/components/knowledgeErrorPlaceholder';
import { KnowledgeLoading } from 'lib-knowledge-utils/components/loading';
import {
  SourceConversationsRenderer,
  SourceConversationsRendererProvider,
} from 'lib-knowledge-utils/components/sourceConversations/index';
import {
  NotificationContext,
  NotificationContextValue,
} from 'lib-knowledge-utils/contexts/notification';
import { RestConfigProvider } from 'lib-knowledge-utils/contexts/restConfig';
import { collectionRootPath } from 'lib-knowledge-utils/routes';
import { featureFlagsAtom } from 'lib-knowledge-utils/stores/featureFlags';
import { Suspense, useEffect } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useRoutes,
} from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';

import {
  AiDraftEventsProvider,
  SubscribeToAiDraftEvents,
} from './articleSuggestions/aiDraftEvents';
import { ArticleSuggestionsPage } from './articleSuggestions/articleSuggestions';
import { ArticlesPage } from './collection/pages/articles';
import CollectionEditPage from './collection/pages/collectionEdit';
import CollectionRootPage from './collection/pages/home';
import { useShowArticleSuggestions } from './hooks/useShowArticleSuggestions';
import { getArticleEditorRoutes } from './routes';
import KnowledgeSettingsPage from './settings/pages';

function CollectionRoutes() {
  const routes = (
    <Routes>
      <Route path="collection" element={<CollectionRootPage />} />
      <Route path="collection/:collectionId" element={<CollectionEditPage />} />
      <Route path="articles" element={<ArticlesPage />} />
    </Routes>
  );
  return routes;
}

function ArticleEditorRoutes() {
  const routes = getArticleEditorRoutes();
  return useRoutes(routes);
}

function ArticleSuggestionsRoutes() {
  // Gated here as well as in the nav. Redirecting rather than rendering nothing:
  // no other route matches this path, so a stale link would otherwise land on an
  // empty pane.
  const showArticleSuggestions = useShowArticleSuggestions();

  return (
    <Routes>
      <Route
        path="article-suggestions"
        element={
          showArticleSuggestions ? (
            <ArticleSuggestionsPage />
          ) : (
            <Navigate to={collectionRootPath.pattern} replace />
          )
        }
      />
    </Routes>
  );
}

function SettingsRoutes() {
  const routes = (
    <Routes>
      <Route path="settings" element={<KnowledgeSettingsPage />} />
    </Routes>
  );
  return routes;
}

type KnowledgeFeatureFlags = {
  aiAutoTranslation: boolean;
  smartArticleCreateMultiConversation: boolean;
};

const ManagementEntry = ({
  notify,
  featureFlags,
  renderSourceConversations,
  subscribeToAiDraftEvents,
}: {
  notify: NotificationContextValue;
  featureFlags: KnowledgeFeatureFlags;
  /**
   * Renders the conversations an AI-assisted article was drafted from, shown by
   * the chip in the article editor header. Supplied by the host app, which alone
   * can resolve conversation display data.
   */
  renderSourceConversations?: SourceConversationsRenderer;
  /**
   * Delivers the web socket's AI draft events, so the suggestions queue follows
   * drafts changed by the AI or another agent. Supplied by the host app, which
   * alone holds the socket. Without it the queue is a snapshot.
   */
  subscribeToAiDraftEvents?: SubscribeToAiDraftEvents;
}): JSX.Element => {
  const routingLocation = useLocation();
  const setFeatureFlags = useSetAtom(featureFlagsAtom);

  useEffect(() => {
    setFeatureFlags({ featureFlags });
  }, [setFeatureFlags, featureFlags]);

  const managementClient = createManagementApolloClient();

  if (
    routingLocation.pathname === '/knowledge' ||
    routingLocation.pathname === '/knowledge/'
  ) {
    return <Navigate to={collectionRootPath.pattern} replace={true} />;
  }

  return (
    <KnowledgeErrorBoundary fallback={<KnowledgeErrorPlaceholder />}>
      <RestConfigProvider>
        <NotificationContext.Provider value={notify}>
          <ApolloProvider client={managementClient}>
            <Suspense fallback={<KnowledgeLoading />}>
              <QueryParamProvider adapter={ReactRouter6Adapter}>
                <LanguageContextProvider>
                  <SourceConversationsRendererProvider
                    value={renderSourceConversations}
                  >
                    <AiDraftEventsProvider value={subscribeToAiDraftEvents}>
                      <ModalProvider>
                        <ArticleEditorRoutes />
                        <CollectionRoutes />
                        <ArticleSuggestionsRoutes />
                        <SettingsRoutes />
                      </ModalProvider>
                    </AiDraftEventsProvider>
                  </SourceConversationsRendererProvider>
                </LanguageContextProvider>
              </QueryParamProvider>
            </Suspense>
          </ApolloProvider>
        </NotificationContext.Provider>
      </RestConfigProvider>
    </KnowledgeErrorBoundary>
  );
};

export type {
  AiDraftEventHandlers,
  SubscribeToAiDraftEvents,
} from './articleSuggestions/aiDraftEvents';

export default ManagementEntry;
